import React, { useEffect, useState } from 'react';
import { Container } from 'reactstrap';
import { getDatabase, off, onValue, ref } from 'firebase/database';
import { MetaTags } from 'react-meta-tags';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { guidelineTypeOptions, initialGuidelineKanbanState } from 'constants/index';
import { processGuidelines } from 'utils/processGuidelines';
import ChangelogModal from 'components/Changelog-Modal';
import KanbanHeader from 'components/Kanban-Header';
import HeaderContext from 'components/Tasks-Filter/TasksFilter.Context';
import Breadcrumbs from '../../components/Breadcrumb';
import ShowComponent from 'components/Show-Component';
import Shimmer from 'components/Shimmer';
import AccordionList from './components/Accordion-List';

const GuidelinesList = () => {
  const { t: translate } = useTranslation();
  const { user } = useAuth();
  const accountId = user?.account;
  const userStatus = user?.adminStatus;
  const isAdminStatus = ['Super Admin', 'Admin'].includes(user.adminStatus);
  const navigate = useNavigate();
  const [guidelines, setGuidelines] = useState({ columns: initialGuidelineKanbanState });
  const [filter, setFilter] = useState(null);
  const [filterTotal, setFilterTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const getGuidelineType = (type) => {
    for (const option of guidelineTypeOptions) {
      if (option.value === type) {
        return option.label;
      }
    }
  };

  useEffect(() => {
    if (!isAdminStatus) {
      navigate('/');
    }
  }, [isAdminStatus, navigate]);

  useEffect(() => {
    setLoading(true);
    if (!accountId) return;

    try {
      const db = getDatabase();
      const guidelinesRef = ref(db, `guidelines/${accountId}`);

      const handleValueChange = async (snapshot) => {
        const data = snapshot.val();

        const guidelinesWithIds = Object.entries(data ?? {}).map(([key, value]) => ({
          ...value,
          id: key,
        }));

        const nonArchivedGuidelines = Object.values(guidelinesWithIds).filter(
          (guideline) => !guideline.archived,
        );

        const processedGuidelines = await processGuidelines({
          receivedGuidelines: nonArchivedGuidelines,
          initialBoard: initialGuidelineKanbanState,
          filter,
          setFilterTotal,
          getGuidelineType,
        });

        setGuidelines({ columns: processedGuidelines });
      };

      onValue(guidelinesRef, handleValueChange);

      return () => {
        off(guidelinesRef, 'value', handleValueChange);
      };
    } catch (error) {
      console.error('Error fetching guidelines:', error);
    } finally {
      setLoading(false);
    }
  }, [accountId, filter]);

  return (
    <div className="page-content">
      <MetaTags>
        <title>{`${translate('Guidelines List')} | Ectools`}</title>
      </MetaTags>
      <Container fluid>
        <Breadcrumbs title={translate('Content')} breadcrumbItem={translate('Guidelines List')} />

        <ChangelogModal />

        <HeaderContext.Provider value={{ setFilter }}>
          <KanbanHeader
            board={guidelines}
            accountId={accountId}
            filterTotal={filterTotal}
            userStatus={userStatus}
            isGuideline
          />
        </HeaderContext.Provider>
        <div className="mt-4">
          <ShowComponent condition={loading}>
            <LoadingAccordions>
              <Shimmer height="10rem" />
              <Shimmer height="10rem" />
              <Shimmer height="10rem" />
              <Shimmer height="10rem" />
              <Shimmer height="10rem" />
              <Shimmer height="10rem" />
              <Shimmer height="10rem" />
              <Shimmer height="10rem" />
              <Shimmer height="10rem" />
            </LoadingAccordions>
          </ShowComponent>
          <ShowComponent condition={!loading}>
            <AccordionList column="Backlog" data={guidelines?.columns[0]} />
            <AccordionList column="Guideline approval" data={guidelines?.columns[1]} />
            <AccordionList column="Content Structure" data={guidelines?.columns[2]} />
            <AccordionList column="Pre review" data={guidelines?.columns[3]} />
            <AccordionList column="Content review" data={guidelines?.columns[4]} />
            <AccordionList column="Internal approval" data={guidelines?.columns[5]} />
            <AccordionList column="Content approval" data={guidelines?.columns[6]} />
            <AccordionList column="Approved" data={guidelines?.columns[7]} />
            <AccordionList column="Published" data={guidelines?.columns[8]} />
          </ShowComponent>
        </div>
      </Container>
    </div>
  );
};

const LoadingAccordions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

export default GuidelinesList;
